import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Flex, Heading, Text, Button, Badge } from '@radix-ui/themes';
import { ArrowLeftIcon, PersonIcon, GearIcon, ComponentPlaceholderIcon, TableIcon } from '@radix-ui/react-icons';
import OrganizationSelector from '../OrganizationSelector';
import { useClinicAdmin } from '../../hooks/useClinicAdmin';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useCustomViewsAccess } from '../customViews/useCustomViews';

interface ClinicLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const ClinicLayout: React.FC<ClinicLayoutProps> = ({ title, subtitle, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isClinicAdmin } = useClinicAdmin();
  const { orgId } = useOrganization();
  // PHA-1168: gated by the custom-views flag and the network-aware design right
  // (an owner of a parent network designs for its clinics), not by useClinicAdmin.
  const customViewsAccess = useCustomViewsAccess(orgId);

  // PHA-1240: the badge and the Users / Profile buttons follow the real clinic-admin check.
  // Designing views is not enough: a Report designer (or a network owner on a child clinic)
  // can design here but gets 403 from Users / Profile.
  const isAdmin = isClinicAdmin;

  const navItems = [
    { label: 'Users', path: '/clinic-settings/users', icon: <PersonIcon /> },
    { label: 'Profile', path: '/clinic-settings/profile', icon: <GearIcon /> },
    { label: 'Devices', path: '/clinic-settings/devices', icon: <ComponentPlaceholderIcon /> },
    ...(customViewsAccess.canDesign
      ? [{ label: 'Views', path: '/clinic-settings/views', icon: <TableIcon /> }]
      : []),
  ];

  return (
    <Box className="min-h-screen bg-slate-1">
      <Box className="border-b border-slate-6 bg-white sticky top-0 z-50">
        <Container size="4">
          <Flex align="center" justify="between" py="3" wrap="wrap" gap="3">
            <Flex align="center" gap="3">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                aria-label="Back to dashboard"
              >
                <ArrowLeftIcon />
              </Button>
              <Heading size="6" className="text-slate-800">Clinic Settings</Heading>
              {isAdmin && (
                <Badge color="green">Clinic Admin</Badge>
              )}
            </Flex>
            <Flex align="center" gap="2" wrap="wrap">
              <OrganizationSelector />
              {navItems.filter((item) => isAdmin || item.path === '/clinic-settings/devices'
                || item.path === '/clinic-settings/views').map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? 'solid' : 'soft'}
                    onClick={() => navigate(item.path)}
                  >
                    {item.icon}
                    {item.label}
                  </Button>
                );
              })}
            </Flex>
          </Flex>
        </Container>
      </Box>

      <Container size="4" className="py-6">
        <Flex direction="column" gap="1" className="mb-5">
          <Heading size="5" className="text-slate-800">{title}</Heading>
          {subtitle && (
            <Text size="2" color="gray">
              {subtitle}
            </Text>
          )}
        </Flex>
        {children}
      </Container>
    </Box>
  );
};

export default ClinicLayout;
